import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatError } from "@/lib/api";
import { ArrowLeft, CheckCircle, XCircle, AlertTriangle, ShieldCheck, Shield, Copy, Download, Trash2, Eye, EyeOff, KeyRound, Loader2 } from "lucide-react";
import PasskeySection from "@/components/security/PasskeySection";

export default function SecurityPage() {  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!user || user === false) { navigate("/login"); return null; }

  return (
    <div data-testid="security-page">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5]">
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <h1 className="text-base font-bold text-[#0A0A0A]">Sicherheit &amp; Verifikation</h1>
      </div>

      <div className="divide-y divide-[#E5E5E5]">
        {/* Email Status */}
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#0A0A0A]">E-Mail-Verifikation</p>
              <p className="text-xs text-[#999] mt-0.5">{user.email}</p>
            </div>
            {user.email_verified ? (
              <div className="flex items-center gap-1 text-green-600"><CheckCircle size={16} /><span className="text-xs">{t("security_extra.verified")}</span></div>
            ) : (
              <div className="flex items-center gap-1 text-amber-500"><AlertTriangle size={16} /><span className="text-xs">Ausstehend</span></div>
            )}
          </div>
          {!user.email_verified && <VerifyEmailButton />}
        </div>

        {/* Age Verification */}
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-[#0A0A0A]">{t("security_extra.avs_label")}</p>
            {user.age_verification_status === "VERIFIED" ? (
              <div className="flex items-center gap-1 text-green-600"><CheckCircle size={16} /><span className="text-xs">{t("security_extra.verified")}</span></div>
            ) : (
              <div className="flex items-center gap-1 text-[#999]"><XCircle size={16} /><span className="text-xs">{user.age_verification_status || "Nicht gestartet"}</span></div>
            )}
          </div>
          {user.age_verification_status !== "VERIFIED" && (
            <InfoCard variant="warning" className="mt-3">
              <p className="text-xs text-amber-900 leading-relaxed">
                <span className="font-semibold">Vorteile der AVS-Verifizierung:</span>
              </p>
              <ul className="mt-1.5 ml-3 text-xs text-amber-900 list-disc space-y-0.5">
                <li>{t("security_extra.full_stx_access")}</li>
                <li>{t("security_extra.verbatim_presale")}</li>
                <li>{t("security_extra.higher_reply_rates")}</li>
              </ul>
              <button
                data-testid="avs-start-btn"
                onClick={() => alert(t("security_extra.avs_manual_hint"))}
                className="mt-3 inline-flex items-center gap-1 h-9 px-3 bg-amber-500 text-[#0A0A0A] text-xs font-semibold hover:bg-amber-400 transition-colors"
              >
                <CheckCircle size={14} /> AVS-Verifizierung starten
              </button>
              <p className="mt-2 text-[10px] text-amber-700">
                Hinweis: Die automatische Verifizierung via ID-Check ist in Vorbereitung. Bis dahin
                bitte den Support kontaktieren.
              </p>
            </InfoCard>
          )}
        </div>

        {/* Two-Factor Authentication */}
        <TwoFactorSection />

        {/* Password Change (iter174k · inline im Profil) */}
        <ChangePasswordSection />

        {/* Passkey / WebAuthn (iter174l · Beta) */}
        <PasskeySection />

        {/* Newsletter-Einstellung (Owner 2026-06) */}
        <NewsletterPreferenceSection />

        {/* Danger-Zone: Konto löschen */}
        {user.role !== "admin" && <DangerZoneDeleteAccount />}
      </div>
    </div>
  );
}

// ─── Newsletter-Einstellung (Owner 2026-06) ───────────────
function NewsletterPreferenceSection() {
  const { user, refreshUser } = useAuth();
  const [enabled, setEnabled] = useState(!!user?.newsletter_opt_in);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => { setEnabled(!!user?.newsletter_opt_in); }, [user?.newsletter_opt_in]);

  const toggle = async () => {
    const next = !enabled;
    setBusy(true); setMsg("");
    try {
      await api.patch("/me/newsletter", { enabled: next });
      setEnabled(next);
      setMsg(next ? "Newsletter aktiviert." : "Vom Newsletter abgemeldet.");
      try { await refreshUser(); } catch { /* ignore */ }
    } catch (e) { setMsg(formatError(e) || "Fehler"); }
    finally { setBusy(false); }
  };

  return (
    <div className="px-4 py-4" data-testid="newsletter-pref-section">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-[#0A0A0A]">Newsletter</p>
          <p className="text-xs text-[#999] mt-0.5">Neuigkeiten und Angebote per E-Mail. Jederzeit abbestellbar.</p>
        </div>
        <button
          data-testid="newsletter-pref-toggle"
          onClick={toggle}
          disabled={busy}
          role="switch"
          aria-checked={enabled}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${enabled ? "bg-[#0A0A0A]" : "bg-[#E5E5E5]"}`}
        >
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-5" : "translate-x-0.5"}`} />
        </button>
      </div>
      {msg && <p className="text-xs text-[#666] mt-2" data-testid="newsletter-pref-msg">{msg}</p>}
    </div>
  );
}


/**
 * ChangePasswordSection — iter174k
 * Inline-Passwortwechsel im Sicherheits-Profil des eingeloggten Users.
 * Erzwingt TOTP-Code wenn 2FA aktiv (vom Backend geprüft).
 */
function ChangePasswordSection() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);       // {enabled: bool}
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [totp, setTotp] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/2fa/status");
        setStatus(data);
      } catch { setStatus({ enabled: false }); }
    })();
  }, []);

  const pwOk = (p) =>
    p && p.length >= 10 &&
    /[A-Z]/.test(p) && /[a-z]/.test(p) && /[0-9]/.test(p) && /[^A-Za-z0-9]/.test(p);

  const submit = async () => {
    setErr(""); setMsg(null);
    if (!current) { setErr("Bitte aktuelles Passwort eingeben."); return; }
    if (!pwOk(next)) {
      setErr("Neues Passwort: mind. 10 Zeichen, je 1 Groß-/Kleinbuchstabe, Zahl, Sonderzeichen.");
      return;
    }
    if (next !== confirm) { setErr("Bestätigung stimmt nicht mit neuem Passwort überein."); return; }
    if (next === current) { setErr("Das neue Passwort muss sich vom aktuellen unterscheiden."); return; }
    if (status?.enabled && !totp) {
      setErr("2FA ist aktiv — bitte 6-stelligen Code aus der Authenticator-App eingeben.");
      return;
    }
    setBusy(true);
    try {
      const payload = { current_password: current, new_password: next };
      if (status?.enabled) payload.totp_code = totp;
      const { data } = await api.post("/auth/change-password", payload);
      setMsg({ ok: true, at: data.changed_at });
      setCurrent(""); setNext(""); setConfirm(""); setTotp("");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const code = d?.code;
      if (code === "WRONG_CURRENT_PASSWORD") setErr("Aktuelles Passwort ist nicht korrekt.");
      else if (code === "TOTP_REQUIRED") setErr("2FA-Code ist erforderlich.");
      else if (code === "TOTP_INVALID") setErr("TOTP-Code ist nicht korrekt.");
      else if (code === "WEAK_PASSWORD") setErr("Passwort erfüllt nicht die Sicherheitsregeln.");
      else if (code === "SAME_AS_CURRENT") setErr("Neues Passwort darf nicht identisch mit dem aktuellen sein.");
      else if (code === "LOCKED") setErr(d.message || "Zu viele Fehlversuche.");
      else setErr(formatError(e) || "Passwort-Wechsel fehlgeschlagen.");
    } finally { setBusy(false); }
  };

  return (
    <div className="px-4 py-4" data-testid="change-password-section">
      <p className="text-sm font-medium text-[#0A0A0A] flex items-center gap-1.5">
        <KeyRound size={14} /> Passwort ändern
      </p>
      <p className="mt-1 text-[11px] text-[#666]">
        Bei aktiver 2FA ist zusätzlich ein Code aus der Authenticator-App nötig.
      </p>

      <div className="mt-3 space-y-2 max-w-md">
        <label className="block">
          <span className="text-[11px] text-[#666]">{t("security_extra.current_pw")}</span>
          <div className="relative">
            <input
              data-testid="change-pw-current"
              type={showCurrent ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              className="mt-0.5 w-full h-10 pr-10 pl-3 border border-[#DDD] text-sm"
            />
            <button
              type="button"
              onClick={() => setShowCurrent((v) => !v)}
              className="absolute right-2 top-2.5 text-[#666]"
              aria-label={showCurrent ? "Passwort verbergen" : "Passwort anzeigen"}
              data-testid="change-pw-current-toggle"
            >
              {showCurrent ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </label>

        <label className="block">
          <span className="text-[11px] text-[#666]">{t("security_extra.new_pw")}</span>
          <div className="relative">
            <input
              data-testid="change-pw-new"
              type={showNext ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              className="mt-0.5 w-full h-10 pr-10 pl-3 border border-[#DDD] text-sm"
            />
            <button
              type="button"
              onClick={() => setShowNext((v) => !v)}
              className="absolute right-2 top-2.5 text-[#666]"
              aria-label={showNext ? "Passwort verbergen" : "Passwort anzeigen"}
              data-testid="change-pw-new-toggle"
            >
              {showNext ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </label>

        <label className="block">
          <span className="text-[11px] text-[#666]">{t("security_extra.confirm_new_pw")}</span>
          <input
            data-testid="change-pw-confirm"
            type={showNext ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            className="mt-0.5 w-full h-10 px-3 border border-[#DDD] text-sm"
          />
        </label>

        {status?.enabled && (
          <label className="block">
            <span className="text-[11px] text-[#666]">2FA-Code (aus Authenticator-App)</span>
            <input
              data-testid="change-pw-totp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={10}
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\s/g, ""))}
              placeholder={t("security_extra.tfa_code_ph")}
              className="mt-0.5 w-full h-10 px-3 border border-[#DDD] text-sm font-mono tracking-widest"
            />
          </label>
        )}

        {err && (
          <InfoCard variant="danger" size="sm" icon={<AlertTriangle size={12} />} testId="change-pw-error">
            {err}
          </InfoCard>
        )}
        {msg?.ok && (
          <InfoCard variant="success" size="sm" icon={<CheckCircle size={12} />} testId="change-pw-success">
            Passwort wurde geändert. Wir haben dir eine Bestätigungs-E-Mail geschickt.
          </InfoCard>
        )}

        <button
          data-testid="change-pw-submit"
          onClick={submit}
          disabled={busy}
          className="h-10 px-4 text-xs font-semibold bg-[#0A0A0A] text-white hover:bg-[#222] disabled:opacity-50 flex items-center gap-1.5"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />}
          Passwort ändern
        </button>
      </div>
    </div>
  );
}


function DangerZoneDeleteAccount() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const emailMatches = confirmEmail.trim().toLowerCase() === (user?.email || "").toLowerCase();

  const doDelete = async () => {
    if (!emailMatches) { setError(t("security_extra.email_mismatch")); return; }
    if (!password) { setError(t("security_extra.password_required")); return; }
    setSubmitting(true); setError("");
    try {
      await api.delete("/me", { data: { password, reason: reason.trim() } });
      await logout();
      navigate("/", { replace: true });
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(d?.message || d || formatError(e));
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="danger-zone" className="px-4 py-4 bg-red-50/40">
      <p className="text-sm font-bold text-red-700 flex items-center gap-1.5">
        <AlertTriangle size={14} /> Gefahrenzone
      </p>
      <p className="text-xs text-[#666] mt-1 leading-relaxed">
        Beim Löschen deines Kontos werden dein Profil, deine aktiven Artikel, Feed-Posts und dein Social-Graph entfernt.
        Bestehende Bestellungen und abgegebene Bewertungen bleiben aus rechtlichen Gründen erhalten (dein Name wird aber überall zu <em>{t("security_extra.deleted_user")}</em>).
        Diese Aktion ist <strong>{t("security_extra.irreversible")}</strong>.
      </p>
      <button
        data-testid="delete-account-open-btn"
        onClick={() => { setShowModal(true); setError(""); setConfirmEmail(""); setPassword(""); setReason(""); }}
        className="mt-3 inline-flex items-center gap-1.5 h-10 px-4 border border-red-300 text-sm text-red-700 hover:bg-red-100 transition-colors"
      >
        <Trash2 size={14} /> Konto löschen
      </button>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="px-5 py-4 border-b border-[#E5E5E5]">
              <p className="text-base font-bold text-red-700 flex items-center gap-1.5">
                <AlertTriangle size={16} /> Konto endgültig löschen
              </p>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-xs text-[#666] leading-relaxed">
                Zur Bestätigung gib bitte deine E-Mail-Adresse und dein Passwort ein.
              </p>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#666] mb-1">
                  E-Mail-Adresse deines Kontos
                </label>
                <input
                  data-testid="delete-confirm-text"
                  type="email"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder={user?.email || ""}
                  className="w-full h-10 px-3 border border-[#E5E5E5] text-sm focus:outline-none focus:border-red-500"
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#666] mb-1">
                  Passwort
                </label>
                <div className="relative">
                  <input
                    data-testid="delete-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-10 px-3 pr-10 border border-[#E5E5E5] text-sm focus:outline-none focus:border-red-500"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    data-testid="delete-password-toggle"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#999] hover:text-[#0A0A0A]"
                    aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-[#666] mb-1">
                  Grund (optional, hilft uns)
                </label>
                <textarea
                  data-testid="delete-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  maxLength={200}
                  className="w-full px-3 py-2 border border-[#E5E5E5] text-sm resize-none"
                />
              </div>
              {error && (
                <InfoCard variant="danger" size="sm">{error}</InfoCard>
              )}
            </div>
            <div className="px-5 py-3 border-t border-[#E5E5E5] flex gap-2 justify-end">
              <button
                data-testid="delete-cancel"
                onClick={() => setShowModal(false)}
                disabled={submitting}
                className="rounded-full h-9 px-4 border border-[#E5E5E5] text-xs text-[#666] hover:bg-[#F5F5F5]"
              >
                Abbrechen
              </button>
              <button
                data-testid="delete-confirm-btn"
                onClick={doDelete}
                disabled={submitting || !emailMatches || !password}
                className="h-9 px-4 bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-40"
              >
                {submitting ? "Lösche…" : "Endgültig löschen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VerifyEmailButton() {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCodeEntry, setShowCodeEntry] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const resend = async () => {
    setLoading(true);
    try {
      await api.post("/auth/resend-verification");
      setSent(true);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async () => {
    setErr(""); setOk(false);
    const t = code.trim();
    if (!t) { setErr("Bitte den Code aus der E-Mail eingeben."); return; }
    setBusy(true);
    try {
      await api.post("/auth/verify-email", { token: t });
      setOk(true);
      setCode("");
      // Nach kurzer Anzeige neu laden, damit der neue Status greift.
      setTimeout(() => { window.location.reload(); }, 1200);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : (d?.message || formatError(e) || "Code ungültig oder abgelaufen."));
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2 space-y-2" data-testid="email-verification-section">
      <div className="flex gap-2 flex-wrap">
        <button
          data-testid="resend-verification-btn"
          onClick={resend}
          disabled={loading || sent}
          className="h-10 px-4 border border-[#E5E5E5] text-sm text-[#0A0A0A] hover:bg-[#F5F5F5] transition-colors disabled:opacity-50"
        >
          {sent ? "E-Mail gesendet" : loading ? "Wird gesendet..." : "Bestätigungs-Link erneut senden"}
        </button>
        <button
          data-testid="toggle-verify-code-btn"
          type="button"
          onClick={() => setShowCodeEntry((v) => !v)}
          className="h-10 px-4 border border-[#E5E5E5] text-sm text-[#0A0A0A] hover:bg-[#F5F5F5] transition-colors"
        >
          {showCodeEntry ? "Code-Eingabe schließen" : "Code manuell eingeben"}
        </button>
      </div>

      {sent && (
        <p className="text-[11px] text-emerald-700" data-testid="verify-sent-hint">
          Wir haben dir eine E-Mail mit einem Bestätigungs-Link geschickt. Klicke einfach auf den Button in der E-Mail.
        </p>
      )}

      {showCodeEntry && (
        <div className="p-3 bg-[#FAFAFA] border border-[#E5E5E5] space-y-2 max-w-md" data-testid="verify-code-panel">
          <p className="text-[11px] text-[#666]">
            Alternativ kannst du den Code aus der E-Mail hier einfügen (nur nötig, wenn du den Link
            nicht anklicken kannst).
          </p>
          <input
            data-testid="verify-code-input"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Code aus der E-Mail"
            autoComplete="one-time-code"
            className="w-full h-10 px-3 border border-[#DDD] bg-white text-sm font-mono"
          />
          {err && (
            <InfoCard variant="danger" size="sm" icon={<AlertTriangle size={12} />} testId="verify-code-error">
              {err}
            </InfoCard>
          )}
          {ok && (
            <InfoCard variant="success" size="sm" icon={<CheckCircle size={12} />} testId="verify-code-success">
              E-Mail bestätigt.
            </InfoCard>
          )}
          <button
            data-testid="verify-code-submit"
            onClick={submitCode}
            disabled={busy || !code.trim()}
            className="h-10 px-4 bg-[#0A0A0A] text-white text-xs font-semibold hover:bg-[#222] disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
            Bestätigen
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Sub-Batch 2FA-1: Two-Factor Authentication Section ─────
function TwoFactorSection() {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null); // {enabled, method, recovery_codes_remaining}
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("idle"); // idle | setup | verifying | enabled-codes | disable | regen-codes
  const [secret, setSecret] = useState("");
  const [qrDataUri, setQrDataUri] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [currentPassword, setCurrentPassword] = useState("");

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/2fa/status");
      setStatus(data);
    } catch {
      setStatus({ enabled: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const beginSetup = async () => {
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup-init");
      setSecret(data.secret);
      setQrDataUri(data.qr_data_uri || "");
      setStage("setup");
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  };

  const verifySetup = async () => {
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup-verify", { code: code.trim() });
      setRecoveryCodes(data.recovery_codes);
      setStage("enabled-codes");
      setCode("");
      await loadStatus();
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  };

  const beginDisable = () => {
    setError(""); setCode(""); setCurrentPassword(""); setStage("disable");
  };

  const submitDisable = async () => {
    setError(""); setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { current_password: currentPassword, code: code.trim() });
      setStage("idle"); setCode(""); setCurrentPassword("");
      await loadStatus();
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  };

  const beginRegen = () => {
    setError(""); setCode(""); setCurrentPassword(""); setStage("regen-codes");
  };

  const submitRegen = async () => {
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/regenerate-recovery-codes",
        { current_password: currentPassword, code: code.trim() });
      setRecoveryCodes(data.recovery_codes);
      setStage("enabled-codes");
      setCode(""); setCurrentPassword("");
      await loadStatus();
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  };

  const downloadCodes = () => {
    if (!recoveryCodes) return;
    const txt = `stilltouched – Wiederherstellungscodes\n\n` +
      `Bewahre diese Codes sicher auf. Jeder Code ist nur einmal verwendbar.\n\n` +
      recoveryCodes.map((c, i) => `${i + 1}. ${c}`).join("\n");
    const blob = new Blob([txt], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "stilltouched-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyCodes = () => {
    if (!recoveryCodes) return;
    navigator.clipboard.writeText(recoveryCodes.join("\n")).catch(() => {});
  };

  if (loading) {
    return <div className="px-4 py-4 text-xs text-[#999]">Lade 2FA-Status...</div>;
  }

  return (
    <div className="px-4 py-4" data-testid="security-2fa-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-[#0A0A0A]" />
          <p className="text-sm font-medium text-[#0A0A0A]">Zwei-Faktor-Authentifizierung (2FA)</p>
        </div>
        {status?.enabled ? (
          <span className="text-xs px-2 py-0.5 border border-green-200 bg-green-50 text-green-700">Aktiv</span>
        ) : (
          <span className="text-xs px-2 py-0.5 border border-[#E5E5E5] bg-[#FAFAFA] text-[#999]">Inaktiv</span>
        )}
      </div>
      <p className="mt-1 text-xs text-[#999]">
        Schützt deinen Login zusätzlich mit einem 6-stelligen Code aus einer Authenticator-App
        (Google Authenticator, Authy, 1Password u.&nbsp;a.).
      </p>

      {/* IDLE: nichts aktiv */}
      {stage === "idle" && !status?.enabled && (
        <button
          data-testid="2fa-enable-btn"
          onClick={beginSetup}
          disabled={busy}
          className="mt-3 inline-flex items-center gap-1.5 rounded-full h-10 px-4 bg-[#0A0A0A] text-white text-sm hover:bg-[#222] disabled:opacity-50"
        >
          <Shield size={14} /> 2FA aktivieren
        </button>
      )}

      {/* IDLE: 2FA bereits aktiv → Manage-Buttons */}
      {stage === "idle" && status?.enabled && (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-[#666]">
            Verbleibende Wiederherstellungscodes: <strong>{status.recovery_codes_remaining}</strong>
          </p>
          <div className="flex gap-2 flex-wrap">
            <button data-testid="2fa-regen-btn" onClick={beginRegen} className="rounded-full h-9 px-3 border border-[#E5E5E5] text-xs hover:bg-[#F5F5F5]">
              Wiederherstellungscodes neu generieren
            </button>
            <button data-testid="2fa-disable-btn" onClick={beginDisable} className="h-9 px-3 border border-red-300 text-red-600 text-xs hover:bg-red-50">
              2FA deaktivieren
            </button>
          </div>
        </div>
      )}

      {/* SETUP: QR + Code-Verify */}
      {stage === "setup" && (
        <div className="mt-3 p-3 bg-[#FAFAFA] border border-[#E5E5E5] space-y-3">
          <p className="text-xs text-[#0A0A0A] font-medium">Schritt 1: Authenticator-App einrichten</p>
          <p className="text-[11px] text-[#666]">
            Scanne den QR-Code mit deiner Authenticator-App – oder gib das Geheimnis manuell ein.
          </p>
          <div className="flex gap-3 items-start">
            <img
              data-testid="2fa-qr-img"
              src={qrDataUri}
              alt="QR-Code"
              className="w-40 h-40 border border-[#E5E5E5] bg-white"
            />
            <div className="flex-1">
              <label className="text-[10px] text-[#999] uppercase tracking-wider">Manueller Code</label>
              <div className="mt-1 px-2 py-2 bg-white border border-[#E5E5E5] font-mono text-xs break-all" data-testid="2fa-secret">{secret}</div>
              <p className="mt-1 text-[10px] text-[#999]">Typ: TOTP · 6 Stellen · 30s</p>
            </div>
          </div>
          <div>
            <label className="text-[10px] text-[#999] uppercase tracking-wider">Schritt 2: Code aus App eingeben</label>
            <input
              data-testid="2fa-setup-code"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="mt-1 w-full h-11 px-3 border border-[#E5E5E5] bg-white text-sm text-center tracking-[0.4em] font-mono focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
              placeholder="123456"
              autoFocus
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button onClick={() => { setStage("idle"); setCode(""); setError(""); }} className="flex-1 h-10 border border-[#E5E5E5] text-sm">{t("security_extra.cancel")}</button>
            <button data-testid="2fa-setup-verify-btn" onClick={verifySetup} disabled={busy || code.length !== 6}
              className="flex-1 rounded-full h-10 bg-[#0A0A0A] text-white text-sm disabled:opacity-50">
              {busy ? "Prüfe..." : "Aktivieren"}
            </button>
          </div>
        </div>
      )}

      {/* RECOVERY CODES DISPLAY (after enable / regen) */}
      {stage === "enabled-codes" && recoveryCodes && (
        <InfoCard variant="warning" className="mt-3 space-y-3">
          <p className="text-xs font-bold text-amber-900">⚠ Wiederherstellungscodes – nur einmal sichtbar</p>
          <p className="text-[11px] text-amber-800 leading-relaxed">
            Speichere diese Codes an einem sicheren Ort. Du kannst sie verwenden, wenn du keinen Zugriff auf deine Authenticator-App hast. Jeder Code ist nur <strong>einmal</strong> gültig.
          </p>
          <div className="grid grid-cols-2 gap-1 font-mono text-xs bg-white p-2 border border-amber-200" data-testid="2fa-recovery-codes">
            {recoveryCodes.map((c, i) => (
              <div key={i} className="px-2 py-1">{c}</div>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={copyCodes} className="h-9 px-3 border border-amber-300 text-xs flex items-center gap-1 hover:bg-amber-100">
              <Copy size={12} /> In Zwischenablage
            </button>
            <button onClick={downloadCodes} className="h-9 px-3 border border-amber-300 text-xs flex items-center gap-1 hover:bg-amber-100">
              <Download size={12} /> Als .txt herunterladen
            </button>
            <button data-testid="2fa-codes-done" onClick={() => { setRecoveryCodes(null); setStage("idle"); }} className="h-9 px-3 bg-[#0A0A0A] text-white text-xs ml-auto">
              Verstanden, fertig
            </button>
          </div>
        </InfoCard>
      )}

      {/* DISABLE */}
      {stage === "disable" && (
        <InfoCard variant="danger" className="mt-3 space-y-3">
          <p className="text-xs font-medium text-red-900">2FA deaktivieren</p>
          <p className="text-[11px] text-red-800">{t("security_extra.confirm_pw_2fa")}</p>
          <input
            data-testid="2fa-disable-password"
            type="password"
            placeholder={t("security_extra.current_password")}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="w-full h-10 px-3 border border-red-200 bg-white text-sm"
          />
          <input
            data-testid="2fa-disable-code"
            type="text"
            placeholder={t("security_extra.code_placeholder")}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="w-full h-10 px-3 border border-red-200 bg-white text-sm font-mono"
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button onClick={() => { setStage("idle"); setCode(""); setCurrentPassword(""); setError(""); }} className="flex-1 h-10 border border-[#E5E5E5] text-sm">{t("security_extra.cancel")}</button>
            <button data-testid="2fa-disable-confirm" onClick={submitDisable} disabled={busy || !currentPassword || !code}
              className="flex-1 h-10 bg-red-600 text-white text-sm disabled:opacity-50">
              {busy ? "..." : "2FA deaktivieren"}
            </button>
          </div>
        </InfoCard>
      )}

      {/* REGENERATE CODES */}
      {stage === "regen-codes" && (
        <div className="mt-3 p-3 bg-[#FAFAFA] border border-[#E5E5E5] space-y-3">
          <p className="text-xs font-medium text-[#0A0A0A]">Wiederherstellungscodes neu generieren</p>
          <p className="text-[11px] text-[#666]">
            Alte Codes werden ungültig. Bestätige mit Passwort + aktuellem 2FA-Code.
          </p>
          <input
            data-testid="2fa-regen-password"
            type="password"
            placeholder={t("security_extra.current_password")}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            className="w-full h-10 px-3 border border-[#E5E5E5] bg-white text-sm"
          />
          <input
            data-testid="2fa-regen-code"
            type="text"
            placeholder="6-stelliger Code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="w-full h-10 px-3 border border-[#E5E5E5] bg-white text-sm font-mono"
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button onClick={() => { setStage("idle"); setCode(""); setCurrentPassword(""); setError(""); }} className="flex-1 h-10 border border-[#E5E5E5] text-sm">{t("security_extra.cancel")}</button>
            <button data-testid="2fa-regen-confirm" onClick={submitRegen} disabled={busy || !currentPassword || !code}
              className="flex-1 rounded-full h-10 bg-[#0A0A0A] text-white text-sm disabled:opacity-50">
              {busy ? "..." : "Neue Codes generieren"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
